// Recharge.jsx
import React, { useState, useCallback, useRef } from "react";
import { ArrowLeft, X, UploadCloud, CheckCircle2, AlertCircle } from "lucide-react";
import axios from "axios";

// ----- Lightweight toast system (no external deps) -----
// Renders stacked, auto-dismissing notifications that match the app's
// dark navy / blue theme instead of the browser's native alert() box.
const Toast = ({ toast, onDismiss }) => {
  const isSuccess = toast.type === "success";
  return (
    <div
      role="alert"
      style={{ backgroundColor: "#132C4A" }}
      className={`pointer-events-auto w-full max-w-[360px] rounded-xl border px-4 py-3 flex items-center gap-3 shadow-2xl
        ${isSuccess ? "border-emerald-500/30" : "border-red-500/30"}
        animate-[toast-in_0.22s_ease-out]
      `}
    >
      {isSuccess ? (
        <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
      ) : (
        <AlertCircle size={18} className="text-red-400 shrink-0" />
      )}
      <p className="text-[13px] text-gray-100 leading-snug flex-1">{toast.message}</p>
      <button
        onClick={() => onDismiss(toast.id)}
        className="text-gray-500 hover:text-white transition shrink-0"
        aria-label="Dismiss notification"
      >
        <X size={15} />
      </button>
    </div>
  );
};

const ToastContainer = ({ toasts, onDismiss }) => (
  <div className="fixed top-0 inset-x-0 z-[999] flex flex-col items-center gap-2 px-4 pt-[calc(0.75rem+env(safe-area-inset-top))] pointer-events-none">
    {toasts.map((t) => (
      <Toast key={t.id} toast={t} onDismiss={onDismiss} />
    ))}
  </div>
);

const useToasts = () => {
  const [toasts, setToasts] = useState([]);
  const idRef = useRef(0);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message, type = "error", duration = 3200) => {
      const id = ++idRef.current;
      setToasts((prev) => [...prev, { id, message, type }]);
      window.setTimeout(() => dismiss(id), duration);
    },
    [dismiss]
  );

  return { toasts, showToast, dismiss };
};
// ----- end toast system -----

const Recharge = ({ onBack }) => {
  const [amount, setAmount] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileUrl, setFileURL] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { toasts, showToast, dismiss } = useToasts();

  const rechargeOptions = [500, 2000, 5000, 10000, 50000];

  const handleFileChange = async (e) => {
    if (e.target.files.length > 0) {
      const file = e.target.files[0];
      setIsUploading(true);
      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/image-upload`,
          { image: file },
          {
            headers: {
              Authorization: "Bearer " + localStorage.getItem("token"),
              "Content-Type": "multipart/form-data",
            },
          }
        );

        setSelectedFile(file);
        setFileURL(res.data.data.url);
        showToast(`Uploaded ${file.name}`, "success");
      } catch (error) {
        console.error("❌ Upload failed:", error);
        showToast("Image upload failed. Please try again.", "error");
      } finally {
        setIsUploading(false);
      }
    }
  };

  const removeFile = () => {
    setSelectedFile(null);
    setFileURL(null);
  };

  const handleSubmit = async () => {
    if (!amount || !selectedFile) {
      showToast("Please select an amount and upload an image.", "error");
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_BASE_URL}/wallet/recharge`,
        { amountRecharged: amount, paymentUrl: fileUrl },
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
          },
        }
      );
      showToast(`Recharge request submitted: ${amount} USDT`, "success");
      setAmount("");
      setFileURL(null);
      setSelectedFile(null);
    } catch (error) {
      showToast("Recharge failed. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-[#0A1A2F] min-h-screen text-white font-sans overflow-y-auto pb-[calc(6rem+env(safe-area-inset-bottom))]">
      <style>{`
        @keyframes toast-in {
          from { opacity: 0; transform: translateY(-8px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      <ToastContainer toasts={toasts} onDismiss={dismiss} />

      {/* Header */}
      <div className="flex items-center px-4 py-3 border-b border-blue-900">
        <button
          onClick={onBack}
          className="text-gray-300 hover:text-white transition"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="flex-1 text-center text-base font-medium text-white">
          Recharge
        </h1>
        <div className="w-6" />
      </div>

      {/* Wallet Addresses */}
      <div className="space-y-3 px-4 mt-6">
        {[
          { label: "USDT (TRC20)", value: "TK4gQ3ob3KaFbso2yTx9Ebu7CwXKB9SinC" },
          {
            label: "USDT (ERC20)",
            value: "0x84323b0a2809e939e97db9d0fd2f75946ee0cd75",
          },
          { label: "BTC", value: "bc1qexlqx0gzeutamdejrz0jmeftqykm7spyugm48d" },
          { label: "ETH", value: "0x84323b0a2809e939e97db9d0fd2f75946ee0cd75" },
        ].map((item, idx) => (
          <div
            key={idx}
            className="bg-[#10243D]/70 rounded-md px-3 py-2 flex justify-between items-center shadow-sm border border-blue-900/30"
          >
            <div>
              <p className="text-xs text-gray-400">{item.label}</p>
              <p className="text-xs break-all text-gray-200">{item.value}</p>
            </div>
            <button
              className="text-blue-400 text-xs px-2 py-1 rounded hover:bg-blue-500/20"
              onClick={() => {
                navigator.clipboard.writeText(item.value);
                showToast("Copied to clipboard", "success", 1800);
              }}
            >
              Copy
            </button>
          </div>
        ))}
      </div>

      {/* Recharge Options */}
      <div className="mt-6 px-4">
        <p className="text-sm text-gray-300 mb-2">Recharge Amount (USDT)</p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {rechargeOptions.map((value) => (
            <button
              key={value}
              onClick={() => setAmount(value)}
              className={`py-2 rounded-md text-sm font-medium transition shadow-md ${amount === value
                ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-blue-500/30"
                : "bg-gray-800 text-gray-300 hover:bg-blue-600 hover:text-white"
                }`}
            >
              {value}
            </button>
          ))}
        </div>

        <input
          type="number"
          placeholder="Or enter custom amount"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="mt-3 w-full px-3 py-2 bg-gray-800 text-base rounded-md outline-none text-white placeholder-gray-400 border border-blue-900/40 focus:ring-1 focus:ring-blue-500"
        />
      </div>

      {/* Upload Proof - FIXED - No stretching */}
      <div className="mt-6 px-4">
        <p className="text-sm text-gray-300 mb-2">Upload Proof</p>
        <div className="w-full h-52 sm:h-32 border-2 border-dashed border-blue-700/60 rounded-md flex items-center justify-center relative bg-gray-900/40 transition-all duration-200 overflow-hidden">
          {selectedFile ? (
            <>
              <img
                src={URL.createObjectURL(selectedFile)}
                alt="Preview"
                className="w-full h-full object-contain rounded-md"
              />
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFile();
                }}
                className="absolute top-1 right-1 bg-gray-800 text-white rounded-full p-1 hover:bg-red-500 z-10"
              >
                <X size={16} />
              </button>
            </>
          ) : (
            <label className="w-full h-full flex flex-col items-center justify-center cursor-pointer">
              <div className="flex flex-col items-center justify-center gap-2">
                <UploadCloud size={32} className="text-blue-400" />
                <span className="text-blue-300 text-sm text-center">
                  {isUploading ? "Uploading…" : "Click to upload image"}
                </span>
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                disabled={isUploading}
              />
            </label>
          )}
        </div>
      </div>

      {/* Submit Button */}
      <div className="mt-8 px-4 flex justify-center">
        <button
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="w-full py-3 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl shadow-lg shadow-blue-800/40 transition"
        >
          {isSubmitting ? "Submitting…" : "Submit"}
        </button>
      </div>

      {/* Bottom spacing for safe scroll */}
      <div className="h-24"></div>
    </div>
  );
};

export default Recharge;